import type { AlertOfferMatched } from "shared/services/alerts/alerts.types";
import { getDeeplink } from "shared/utils/DeepLink/getDeepLink";
import type {
  SERPFlightSector,
  SERPSellerResult,
} from "shared/types/serpResult";

/**
 * Cria a data em hora local à meia-noite: `converterData` (getDeeplink) lê
 * dia/mês/ano locais, então uma data em UTC voltaria um dia em fusos negativos.
 */
const toLocalDate = (ymd: string): Date | null => {
  const match = ymd.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const [, y, m, d] = match.map(Number);
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y &&
    date.getMonth() === m - 1 &&
    date.getDate() === d
    ? date
    : null;
};

const createSector = (
  index: number,
  airline: string,
  date: Date,
  origin: string,
  destination: string,
): SERPFlightSector => ({
  index,
  airline,
  airlineLogo: "",
  departureDate: date,
  departureTime: date,
  departureTimeDifferentDay: null,
  departureAirportIata: origin,
  departureDayOfWeek: date.getDay(),
  stops: 0,
  totalDuration: 0,
  arrivalDate: date,
  arrivalTime: date,
  arrivalTimeDifferentDay: null,
  arrivalAirportIata: destination,
  arrivalDayOfWeek: date.getDay(),
  flights: [],
});

/**
 * Monta o link da página /redirect do Voopter para a passagem encontrada,
 * como o app faz no card do alerta (offerToDeepLinkParams + getDeeplink).
 * Retorna null se a oferta não tiver o mínimo para gerar o link.
 */
export function getMatchedOfferUrl(offer: AlertOfferMatched): string | null {
  const departure = toLocalDate(offer.departureDate);
  const returning = offer.returnDate ? toLocalDate(offer.returnDate) : null;
  const airline = offer.departureAirlineCode ?? offer.departureAirline;

  if (
    !departure ||
    !offer.voopterRedirectUrl ||
    !offer.webserviceId ||
    !offer.sellerName ||
    !offer.originAirport ||
    !offer.destinationAirport ||
    !airline
  ) {
    return null;
  }

  const sectors = [
    createSector(
      0,
      airline,
      departure,
      offer.originAirport,
      offer.destinationAirport,
    ),
  ];
  if (returning) {
    sectors.push(
      createSector(
        1,
        offer.departureAirlineCode ?? airline,
        returning,
        offer.destinationAirport,
        offer.originAirport,
      ),
    );
  }

  const seller: SERPSellerResult = {
    sellerId: offer.webserviceId,
    sellerName: offer.sellerName,
    sellerImg: "",
    deepLink: offer.voopterRedirectUrl,
    price: offer.lowestAdultPrice,
    uniqueID: offer.id,
    integrationName: offer.webserviceName ?? offer.webserviceId,
  };

  return getDeeplink(
    { sectors, minPrice: offer.lowestAdultPrice, sellers: [seller] },
    seller,
  );
}
